import { useState } from "react";
import { toast } from "sonner";
import { MailCheck, Send, ShieldCheck } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const inputCls = "w-full bg-[#050505] border border-white/10 px-3 py-2.5 font-mono text-xs text-zinc-100 rounded-none focus:outline-none focus:ring-1 focus:ring-nc-blue focus:border-nc-blue";

export default function EmailVerifyCard({ email, onVerified, testid = "email-verify-card" }) {
  const [code, setCode] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const send = async () => {
    setBusy(true);
    try {
      await api.post("/auth/email/send-code");
      setSent(true);
      toast.success("Verification code sent — check your inbox");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    setBusy(true);
    try {
      await api.post("/auth/email/verify", { code: code.trim() });
      toast.success("Email verified — full access unlocked");
      onVerified?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid={testid} className="border border-nc-orange/50 bg-nc-orange/5 p-5 flex flex-col gap-4">
      <div className="flex items-center gap-3">
        <MailCheck className="w-5 h-5 text-nc-orange shrink-0" strokeWidth={1.75} />
        <div>
          <p className="font-sans font-bold uppercase text-sm text-zinc-100">Verify your email to continue</p>
          <p className="font-mono text-[10px] text-zinc-500 mt-0.5">Security requirement for sales reps and contractors — one code, one minute, done forever.</p>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-mono text-[11px] text-zinc-300 border border-white/15 bg-black/40 px-3 py-2">{email}</span>
        <button data-testid={`${testid}-send`} onClick={send} disabled={busy} className="font-mono text-[10px] tracking-[0.2em] uppercase bg-nc-blue hover:bg-nc-blue/80 disabled:opacity-40 text-white px-4 py-2.5 transition-colors flex items-center gap-1.5">
          <Send className="w-3.5 h-3.5" /> {sent ? "Resend Code" : "Send Code"}
        </button>
      </div>
      {sent && (
        <div className="flex items-center gap-2">
          <input
            data-testid={`${testid}-code`}
            className={`${inputCls} max-w-[180px] tracking-[0.4em] text-center text-base`}
            placeholder="000000"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
          />
          <button data-testid={`${testid}-confirm`} onClick={verify} disabled={busy || code.trim().length !== 6} className="font-mono text-[10px] tracking-[0.2em] uppercase bg-nc-green hover:bg-nc-green/80 disabled:opacity-40 text-white px-4 py-2.5 transition-colors flex items-center gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5" /> Verify
          </button>
        </div>
      )}
    </div>
  );
}
